<template>
  <div class="sidebar-root d-flex flex-column fill-height pa-12">
    <div class="sidebar-header d-flex align-center pb-10 mb-10">
      <div class="logo-box mr-2"/>
      <div class="logo-text text-h5 font-weight-bold">
        mank
      </div>
    </div>

    <v-list dense nav class="pa-0 sidebar-nav-list">
      <v-list-item :to="{ name: 'Dashboard' }" exact class="mb-6">
        <v-list-item-icon class="mr-4">
          <v-icon>mdi-view-dashboard-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            Dashboard
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item :to="{ name: 'Agents' }" class="mb-6">
        <v-list-item-icon class="mr-4">
          <v-icon>mdi-headset</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            Agents
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item :to="{ name: 'AgentConfigs' }" class="mb-6">
        <v-list-item-icon class="mr-4">
          <v-icon>mdi-cog</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            Agent Config
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item
        v-if="isSuperuser"
        :to="{ name: 'AgentModels' }"
        class="mb-6">
        <v-list-item-icon class="mr-4">
          <v-icon>mdi-brain</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            Agent Models
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item :to="{ name: 'TTS' }" class="mb-6">
        <v-list-item-icon class="mr-4">
          <Icon name="volume-up" variant="solid" size="24" color="muted"/>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            TTS
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item :to="{ name: 'STT' }" class="mb-6">
        <v-list-item-icon class="mr-4">
          <Icon name="text-box" variant="solid" size="24" color="muted"/>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            STT
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item
        v-if="isSuperuser"
        :to="{ name: 'SipTrunk' }"
        class="mb-6">
        <v-list-item-icon class="mr-4">
          <v-icon>mdi-phone-in-talk-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            SIP Trunk
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item :to="{ name: 'ApiTokens' }" class="mb-6">
        <v-list-item-icon class="mr-4">
          <v-icon>mdi-key</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            API Token
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item to="/call-history" class="mb-6">
        <v-list-item-icon class="mr-4">
          <v-icon>mdi-phone-clock</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            Call History
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item
        v-if="showDesignSystem"
        :to="{ name: 'DesignSystemShowcase' }"
        class="mb-6">
        <v-list-item-icon class="mr-4">
          <v-icon>mdi-palette-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="text-element-2 text-semibold">
            Design system
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <div class="mt-auto user-section">
      <v-menu
        v-model="userMenuOpen"
        offset-y
        top
        left
        content-class="user-menu"
        transition="slide-y-transition"
        :close-on-content-click="true">
        <template #activator="{ on, attrs }">
          <div
            class="user-info"
            v-bind="attrs"
            v-on="on">
            <v-avatar size="36" class="user-avatar">
              <span class="text-element-2 text-semibold">{{ userInitials }}</span>
            </v-avatar>
            <div class="user-text">
              <span class="user-name text-element-2 text-semibold grey-dark--text">
                {{ userName }}
              </span>
              <span class="user-email text-element-4 grey--text">
                {{ userEmail }}
              </span>
            </div>
            <v-icon size="20" class="user-chevron grey--text" :class="{ 'chevron-open': userMenuOpen }">
              mdi-chevron-up
            </v-icon>
          </div>
        </template>

        <v-list dense class="user-menu-list">
          <v-list-item :to="{ name: 'Settings' }" class="user-menu-item" @click="userMenuOpen = false">
            <v-list-item-icon class="mr-2">
              <v-icon size="20">
                mdi-cog-outline
              </v-icon>
            </v-list-item-icon>
            <v-list-item-title class="text-element-2 text-semibold">
              Settings
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import isDesignSystemDev from '@/config/devShowcase';
import Icon from './Icon.vue';

export default {
  name: 'SidebarNav',
  components: { Icon },
  data() {
    return { userMenuOpen: false };
  },
  computed: {
    ...mapGetters('auth', ['user', 'isSuperuser']),
    showDesignSystem() {
      return isDesignSystemDev;
    },
    userInitials() {
      if (!this.user) return 'U';

      const firstName = this.user.given_name || '';
      const lastName = this.user.family_name || '';

      if (firstName && lastName) {
        return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
      }

      if (firstName && lastName) {
        const name = firstName + ' ' + lastName;
        const nameParts = name.split(' ');
        if (nameParts.length >= 2) {
          return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
        }
        return nameParts[0].charAt(0).toUpperCase();
      }

      return this.user.email ? this.user.email.charAt(0).toUpperCase() : 'U';
    },
    userName() {
      return this.user?.name || this.user?.email || 'User';
    },
    userEmail() {
      return this.user?.email || '';
    },
  },
  methods: {},
};
</script>

<style lang="scss" scoped>
.sidebar-root {
  width: 240px;
  background: $light-surface-1;
  color: $light-foreground-1;
}

.logo-box {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background-image: url('./../assets/app-logo.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.logo-text {
  color: $light-foreground-1;
}
/* Sidebar nav list: ikonlar aktif değilse silik (::v-deep ile Vuetify child'larına uygulanır) */
.sidebar-nav-list::v-deep .v-list-item:not(.v-list-item--active) .v-list-item__icon,
.sidebar-nav-list::v-deep .v-list-item:not(.v-list-item--active) .v-list-item__icon .v-icon {
  color: $light-foreground-4 !important;
}
.sidebar-nav-list::v-deep .v-list-item:not(.v-list-item--active) .v-list-item__icon .icon-component {
  color: $light-foreground-4 !important;
}
.sidebar-nav-list::v-deep .v-list-item--active .v-list-item__icon,
.sidebar-nav-list::v-deep .v-list-item--active .v-list-item__icon .v-icon {
  color: $light-foreground-1 !important;
}
.sidebar-nav-list::v-deep .v-list-item--active .v-list-item__icon .icon-component {
  color: $light-foreground-1 !important;
}

.v-list-item{
  border-radius: 8px;
  min-height: 44px;
  overflow: visible;
  .v-list-item__content {
    overflow: visible;
  }
  &--active {
    background: $light-surface-2;
    color: $light-foreground-1;
    &:before{
      opacity: 0;
    }
    .v-list-item__title{
      color: $light-foreground-1 !important;
    }
  }
}

.user-section {
  width: 100%;
  padding-top: 12px;
  border-top: 1px solid $light-border;
}

.user-info {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  min-height: 56px;
  gap: 12px;

  &:hover {
    background-color: $light-surface-2;
  }
}

.user-avatar {
  flex-shrink: 0;
  background-color: $light-surface-2 !important;
  color: $light-primary-neutral !important;
  border: 1px solid $light-border !important;
}

.user-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.user-name,
.user-email {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-chevron {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.user-chevron.chevron-open {
  transform: rotate(180deg);
}

.user-menu-list {
  min-width: 160px;
  padding: 4px !important;

  .user-menu-item {
    border-radius: 6px;

    .v-icon {
      color: $light-foreground-4;
    }

    &:hover .v-icon {
      color: $light-foreground-1;
    }
  }

}

.v-list-item__title{
  font-size: $body-3-size;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.0;
  color: $light-foreground-4;
  padding-top: 2px;
  padding-bottom: 2px;
  overflow: visible;
}

.grey--text{
  color: $light-foreground-4 !important;
}
.grey-dark--text{
  color: $light-foreground-3 ;
}

</style>
